import { RefObject, useEffect, useRef, useState } from 'react'
import { useInView } from './use-in-view'

type Options = {
  threshold?: number
  rootMargin?: string
  dwellMs?: number
}

/**
 * Returns true once the referenced element has been continuously visible
 * (at `threshold`) for at least `dwellMs`. One-shot: never resets back to
 * false, so it also acts as its own per-mount dedup.
 */
export const useDwellInView = (
  ref: RefObject<Element | null>,
  { threshold = 0.5, rootMargin = '0px', dwellMs = 1000 }: Options = {}
): boolean => {
  const inView = useInView(ref, { threshold, rootMargin })
  const [dwelled, setDwelled] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (dwelled) return

    if (inView) {
      timerRef.current = setTimeout(() => setDwelled(true), dwellMs)
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [inView, dwelled, dwellMs])

  return dwelled
}
